import { ReactNode, useRef, useEffect, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
} from '@/components/ui/table'
import { LoadingMessage } from '@/components/loading-message'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export interface TableColumn<T> {
  id: string
  header:
    | ReactNode
    | ((props: {
        onSort?: () => void
        isSorted?: boolean
        sortDirection?: 'asc' | 'desc'
      }) => ReactNode)
  cell: (item: T) => ReactNode
  width?: string
  sortable?: boolean
}

export interface DataTableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  keyExtractor: (item: T) => string | number
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
  emptyStateMessage?: string
  endStateMessage?: string
  containerClassName?: string

  // Custom header option - takes precedence over standard header options
  customHeader?: ReactNode

  // Standard header options
  title?: ReactNode
  titleSize?: 'sm' | 'md' | 'lg'
  leftSection?: ReactNode
  rightSection?: ReactNode
  headerInfo?: ReactNode
  compact?: boolean

  // Sorting
  currentSort?: { id: string; direction: 'asc' | 'desc' }
  onSortChange?: (columnId: string) => void

  // Loading message
  loadingMessage?: string

  // Test id
  'data-testid'?: string
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  emptyStateMessage = 'No data found',
  endStateMessage = 'End of list',
  containerClassName,

  // Custom header
  customHeader,

  // Standard header options
  title,
  titleSize = 'md',
  leftSection,
  rightSection,
  headerInfo,

  // Sorting
  currentSort,
  onSortChange,
  compact = true,

  // Loading message
  loadingMessage = 'Loading data...',

  // Test id
  'data-testid': testId,
}: DataTableProps<T>) {
  const observerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || !fetchNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchNextPage()
        }
      },
      { root: null, rootMargin: '100px', threshold: 0.1 }
    )

    if (observerRef.current) {
      observer.observe(observerRef.current)
    }

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Define title size classes
  const titleSizeClasses = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  }

  // Memoize table rows to prevent unnecessary re-renders
  const memoizedRows = useMemo(() => {
    return data.map((item) => (
      <TableRow
        key={keyExtractor(item)}
        className="hover:bg-muted/20"
        data-testid={testId ? `${testId}-row-${keyExtractor(item)}` : undefined}
      >
        {columns.map((column) => (
          <TableCell
            key={`${keyExtractor(item)}-${column.id}`}
            className={cn(compact && 'py-3')}
          >
            {column.cell(item)}
          </TableCell>
        ))}
      </TableRow>
    ))
  }, [data, columns, keyExtractor, compact, testId])

  return (
    <Card
      className={cn('border-0 shadow-none bg-transparent', containerClassName)}
      data-testid={testId}
    >
      <CardContent className="p-0 space-y-4">
        {/* Use custom header if provided */}
        {customHeader}

        {/* Otherwise use standard header components if any are provided */}
        {!customHeader && (title || leftSection || rightSection) && (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-4 mr-2">
              {title && (
                <h1 className={cn('font-medium', titleSizeClasses[titleSize])}>
                  {title}
                </h1>
              )}
              {leftSection && (
                <div className="flex-shrink-0">{leftSection}</div>
              )}
            </div>
            {rightSection && (
              <div className="flex-1 ml-auto">{rightSection}</div>
            )}
          </div>
        )}

        {/* Secondary information row */}
        {!customHeader && headerInfo && (
          <div className="flex items-center justify-between px-1 py-1 text-sm">
            {headerInfo}
          </div>
        )}

        {/* Table content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingMessage message={loadingMessage} centered />
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-lg font-medium mb-2">{emptyStateMessage}</div>
          </div>
        ) : (
          <div className="w-full rounded-t-xl rounded-b-none border dark:border-slate-600 dark:border-[1.5px] border-b-0 shadow-sm overflow-hidden bg-card">
            <Table data-testid={testId ? `${testId}-table` : undefined}>
              <TableHeader>
                <TableRow
                  className="bg-muted/30"
                  data-testid={testId ? `${testId}-header-row` : undefined}
                >
                  {columns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={cn(
                        column.width,
                        column.sortable && 'cursor-pointer'
                      )}
                      onClick={() => {
                        if (column.sortable && onSortChange) {
                          onSortChange(column.id)
                        }
                      }}
                    >
                      {typeof column.header === 'function'
                        ? column.header({
                            onSort:
                              column.sortable && onSortChange
                                ? () => onSortChange(column.id)
                                : undefined,
                            isSorted: currentSort?.id === column.id,
                            sortDirection:
                              currentSort?.id === column.id
                                ? currentSort.direction
                                : undefined,
                          })
                        : column.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody data-testid={testId ? `${testId}-body` : undefined}>
                {memoizedRows}
              </TableBody>
            </Table>
            <div className="text-center">
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <LoadingMessage
                    message={`Loading more ${loadingMessage.toLowerCase().replace('loading ', '')}`}
                    centered
                  />
                </div>
              )}
              {!hasNextPage && (
                <div>
                  <p className="text-xs text-muted-foreground/90 py-2">
                    {endStateMessage}
                  </p>
                </div>
              )}
            </div>
            {fetchNextPage && <div ref={observerRef} className="h-10" />}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
